// The Data view's quick eval: `c = a * b + 1` makes (or replaces) data group c from the others, point by point;
// `c += a * 2` (-= *= /= %=) changes c's points, as a Data math step does.
// The right side is varexpr.ts's arithmetic (numbers, + - * / %, unary minus, brackets) over data group names; a
// name that isn't an identifier is written in brackets, [my group]. Names that aren't data groups can be the
// points' own measures (MEASURES: position, density, capture distance, depth edges), worked out by the page. Pure.

import { type Data, type DataSets, newData } from './datagroups.ts';
import { GROUP_NAME } from './log.ts';
import { type Expr, exprLoops, exprNames, parseExpr } from './varexpr.ts';

/**
 * The measures a quick eval can read where no data group has the name: what each is, and the data step source
 * (macros.ts DATA_SRC, its fields) the page works it out by; none: the point's position (x, y, z).
 */
export const MEASURES: Record<string, { what: string; src?: Record<string, string> }> = {
  x: { what: 'the point\'s x (m)' },
  y: { what: 'the point\'s y (m)' },
  z: { what: 'the point\'s z (m)' },
  density5: { what: 'point density within 5 cm', src: { src: 'density', dr: '0.05' } },
  distance: { what: 'capture distance (m)', src: { src: 'range' } },
  depth: { what: 'depth edge strength (0 to 1)', src: { src: 'edges', side: 'abs' } },
  depthSigned: { what: 'signed depth edge (-1 background to 1 foreground)', src: { src: 'edges', side: 'signed' } },
};

/** The measures q reads: the names it uses that are no data group in sets. */
export const measuresIn = (q: DataExpr, sets: DataSets) => q.uses.filter(u => !sets[u] && Object.hasOwn(MEASURES, u));

/** A measure's values: per cloud k, point i (NaN: not known). */
export type Measure = (k: number, i: number) => number;

/**
 * A parsed quick eval: the group it makes (op: changes), the arithmetic (with op, `name op (the right side)`), and
 * the names it reads (each once).
 */
export interface DataExpr { name: string; op?: '+' | '-' | '*' | '/' | '%'; e: Expr; uses: string[] }

/** src parsed, or why it can't be. */
export function parseDataExpr(src: string): DataExpr | string {
  const eq = src.indexOf('=');
  if (eq < 0) return 'write it as name = expression (say c = a * b, or c += a)';
  const op = '+-*/%'.includes(src[eq - 1] ?? '=') ? (src[eq - 1] as DataExpr['op']) : undefined;   // c += …
  let name = src.slice(0, op ? eq - 1 : eq).trim();
  if (name.startsWith('[') && name.endsWith(']')) name = name.slice(1, -1).trim();
  if (!GROUP_NAME.test(name)) return `"${name}" isn't a data group name: letters, digits, space . : + -`;
  // [names] become placeholder identifiers for the parser
  const odd: string[] = [];
  let bad = '';
  const rhs = src.slice(eq + 1).replace(/\[([^\]]*)\]/g, (_, n: string) => {
    const t = n.trim();
    if (!GROUP_NAME.test(t)) bad ||= t;
    let k = odd.indexOf(t);
    if (k < 0) k = odd.push(t) - 1;
    return ` __g${k} `;
  });
  if (bad) return `"${bad}" isn't a data group name`;
  if (!rhs.trim()) return 'nothing after =';
  const e = parseExpr(rhs);
  if (!e) return `can't read "${src.slice(eq + 1).trim()}": numbers, data group names ([in brackets] with spaces), + - * / %, brackets`;
  if (exprLoops(e).length) return '#refs are for For each steps, not here';
  const real = (v: string) => { const m = /^__g(\d+)$/.exec(v); return m ? odd[+m[1]] : v; };
  const rename = (e: Expr): Expr => ('v' in e ? { v: real(e.v) } : 'neg' in e ? { neg: rename(e.neg) } : 'op' in e ? { ...e, a: rename(e.a), b: rename(e.b) } : e);
  const r = op ? { op, a: { v: name }, b: rename(e) } : rename(e);
  return { name, ...(op ? { op } : {}), e: r, uses: exprNames(r) };
}

/** The expression as a function of the values it reads (slot: a name's place in them). */
function compile(e: Expr, slot: (name: string) => number): (x: Float64Array) => number {
  if ('n' in e) { const n = e.n; return () => n; }
  if ('v' in e) { const k = slot(e.v); return x => x[k]; }
  if ('loop' in e) return () => NaN;
  if ('neg' in e) { const f = compile(e.neg, slot); return x => -f(x); }
  const a = compile(e.a, slot), b = compile(e.b, slot);
  switch (e.op) {
    case '+': return x => a(x) + b(x);
    case '-': return x => a(x) - b(x);
    case '*': return x => a(x) * b(x);
    case '/': return x => a(x) / b(x);
    case '%': return x => a(x) % b(x);
  }
}

/**
 * q's group over n clouds, from the groups in sets and the measures (sel: the selection per cloud). Its points:
 * those in any group q reads, each group reading as its default where a point isn't in it; reading none, the
 * points of q's own group as it is, or else the selection. Its default: q on the groups' defaults (0 if no number,
 * as when it reads a measure); a point that comes to no number (÷ 0, a measure not known) takes the default. With
 * an op (c += …): c's points alone, its default kept, a point that comes to no number keeping its value. A string:
 * why there is none.
 */
export function evalDataExpr(q: DataExpr, sets: DataSets, n: number, sel: (Uint8Array | null)[], measures: Record<string, Measure> = {}): Data | string {
  const own = sets[q.name];
  if (q.op && !own) return `no data group "${q.name}" to change (make it with =)`;
  const missing = q.uses.find(u => !sets[u] && !measures[u]);
  if (missing !== undefined) return `no data group "${missing}"${Object.hasOwn(MEASURES, missing) ? '' : ` (nor a measure: ${Object.keys(MEASURES).join(', ')})`}`;
  const groups = q.uses.filter(u => sets[u]), mn = q.uses.filter(u => !sets[u]), order = [...groups, ...mn];   // the values read: the groups', then the measures'
  const gs = groups.map(u => sets[u]), ms = mn.map(u => measures[u]), f = compile(q.e, v => order.indexOf(v));
  const x = new Float64Array(gs.length + ms.length);
  gs.forEach((g, j) => { x[j] = g.dflt; });
  x.fill(NaN, gs.length);
  const d0 = f(x), dflt = q.op ? own.dflt : Number.isFinite(d0) ? d0 : 0;
  const from = q.op ? [own] : gs.length ? gs : own ? [own] : null;
  const masks: (Uint8Array | null)[] = [];
  for (let k = 0; k < n; k++) {
    if (!from) { masks.push(sel[k] && sel[k]!.some(Boolean) ? sel[k] : null); continue; }
    let m: Uint8Array | null = null;
    for (const g of from) {
      const a = g.v[k];
      if (!a) continue;
      m ??= new Uint8Array(a.length);
      for (let i = 0; i < a.length; i++) if (a[i] === a[i]) m[i] = 1;
    }
    masks.push(m);
  }
  if (!masks.some(Boolean)) return from ? 'the groups it reads have no points' : 'select some points first (it reads no data group)';
  return newData(masks, dflt, (k, i) => {
    for (let j = 0; j < gs.length; j++) { const v = gs[j].v[k]?.[i]; x[j] = v === undefined || v !== v ? gs[j].dflt : v; }
    for (let j = 0; j < ms.length; j++) x[gs.length + j] = ms[j](k, i);
    const y = f(x);
    return q.op && !Number.isFinite(y) ? x[0] : y;   // c op …: c's value is x[0] (the first name read)
  });
}

/** The text a Data eval step's name, op ('=', '+', '-', '*', '/', '%') and right side make: `[name] += a * b`. */
export const evalText = (name: string, op: string, rhs: string) => `[${name}] ${op === '=' ? '' : op}= ${rhs}`;

/** The data groups a right side reads (its names but the measures), in order; null: it can't be read. */
export function exprIns(rhs: string): string[] | null {
  const q = parseDataExpr(evalText('x', '=', rhs));
  return typeof q === 'string' ? null : q.uses.filter(u => !Object.hasOwn(MEASURES, u));
}

const IDENT = /^[A-Za-z_]\w{0,39}$/;

/** e written out, a name not an identifier in brackets (each name what sub gives it). */
export function dataExprText(e: Expr, sub: (name: string) => string = n => n): string {
  const go = (e: Expr): string => {
    if ('n' in e) return e.n < 0 ? `(${e.n})` : String(e.n);
    if ('v' in e) { const s = sub(e.v); return IDENT.test(s) ? s : `[${s}]`; }
    if ('loop' in e) return `#${e.loop}`;
    if ('neg' in e) return `-(${go(e.neg)})`;
    return `(${go(e.a)} ${e.op} ${go(e.b)})`;
  };
  return go(e);
}

/** A right side with names given others (to: name → its new name; the rest kept); as it was when it can't be read. */
export function renameIns(rhs: string, to: Record<string, string>): string {
  const q = parseDataExpr(evalText('x', '=', rhs));
  return typeof q === 'string' ? rhs : dataExprText(q.e, n => (Object.hasOwn(to, n) ? to[n] : n));
}
